/**
 * Convert a boundary defined in tile coordinates { x, y, w, h } to pixel min/max bounds.
 * Returns null if the boundary is unset or has zero dimensions.
 */
export const tileBoundaryToPixels = (boundary, tileW, tileH) => {
  if (!boundary?.w || !boundary?.h) return null
  return {
    minX: boundary.x * tileW,
    maxX: (boundary.x + boundary.w) * tileW - tileW,
    minY: boundary.y * tileH,
    maxY: (boundary.y + boundary.h) * tileH - tileH,
  }
}

export const isOutOfBounds = (pos, boundary) =>
  !!boundary && (
    pos.x < boundary.minX || pos.x > boundary.maxX ||
    pos.y < boundary.minY || pos.y > boundary.maxY
  )

/** Current hour (0-23) in the given IANA timezone, falling back to local time. */
export const getLocalHour = (timezone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || undefined,
      hour: 'numeric',
      hour12: false,
    }).formatToParts(new Date())
    const h = parseInt(parts.find(p => p.type === 'hour')?.value ?? '', 10)
    return isNaN(h) ? new Date().getHours() : h % 24
  } catch {
    return new Date().getHours()
  }
}

/** Milliseconds until the next full hour in the given timezone. */
export const msUntilHourChange = (timezone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || undefined,
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date())
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
    const second = parseInt(parts.find(p => p.type === 'second')?.value ?? '0', 10)
    return (60 - minute) * 60_000 - second * 1_000
  } catch {
    const now = new Date()
    return (60 - now.getMinutes()) * 60_000 - now.getSeconds() * 1_000
  }
}

/** True if any boundary entry has hour restrictions. */
export const hasTimedBoundary = (boundary) =>
  Array.isArray(boundary) && boundary.some(b => b.hours)

/**
 * Returns the first boundary entry active at the current local hour,
 * or null if none match (NPC should not be present).
 *
 * boundary can be:
 *   - null/undefined → null (no constraint, but caller handles as "roam free")
 *   - legacy single object { x,y,w,h } → returned as-is (always active)
 *   - array of { x,y,w,h, hours?: [start,end] } → first match for current hour
 */
export const getActiveBoundary = (boundary, timezone) => {
  if (!boundary) return null
  if (!Array.isArray(boundary)) return boundary  // legacy single object

  const hour = getLocalHour(timezone)
  for (const b of boundary) {
    if (!b.hours) return b  // no hours restriction → always active
    const [start, end] = b.hours
    const active = start <= end
      ? hour >= start && hour < end
      : hour >= start || hour < end  // midnight-wrapping range e.g. [22, 6]
    if (active) return b
  }
  return null  // no boundary matches current hour
}
